import React, { Component } from "react";
import { connect } from "react-redux";

import { Translate, I18n } from "react-redux-i18n";

import { momentModule } from "../../utils/Utils";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const { moment, formatDate } = momentModule;

const renderShape = (key, radius, fill) => props => {
  const { x, y, width, height } = props;

  return (
    <svg x={x} y={y} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="energy" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#919DC3" stopOpacity="1" />
          <stop offset="100%" stopColor="#353841" stopOpacity="1" />
        </linearGradient>
        <linearGradient id="time" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="transparent" stopOpacity="1" />
          <stop offset="100%" stopColor="transparent" stopOpacity="1" />
        </linearGradient>
      </defs>
      <rect
        width={width}
        height={height}
        fill={`url(#${key})`}
        rx={Math.round(width - width * radius)}
      />
      {key === "time" && (
        <rect width={width} height={height} fill={fill} rx={Math.round(width - width * radius)} />
      )}
    </svg>
  );
};

class Graph extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  parseData(data) {
    let dates = this.renderTickValues();
    data.forEach(session => {
      const sessionDate = moment(session.startDate).format("DD/MM/YYYY");
      dates[sessionDate] = {
        ...dates[sessionDate],
        energy: (dates[sessionDate]?.energy || 0) + session.totalPower,
        timeCharged: (dates[sessionDate]?.timeCharged || 0) + session.timeCharged,
      };
    });

    return this.adjustEnergyScale(Object.values(dates));
  }

  adjustEnergyScale(data) {
    return data.map(dayData => ({
      ...dayData,
      energy:
        this.props.data.averageEnergyChargedSession > 1 ? dayData.energy / 1000 : dayData.energy,
    }));
  }

  renderTickValues() {
    let dates = {};
    const from = moment(this.props.from).startOf("day");
    const to = moment(this.props.to).endOf("day");

    while (from.isSameOrBefore(to)) {
      dates = {
        ...dates,
        [from.format("DD/MM/YYYY")]: { day: formatDate(from, "date"), energy: 0, timeCharged: 0 },
      };
      from.add(1, "day");
    }

    return dates;
  }

  adjustBarShape(daysLength) {
    const maxRadius = 0.87;
    const ratio = 0.0145;
    let days = daysLength;
    let angle = days * ratio;
    let finalRadius = 0.45;

    if (days >= 1 && days <= 31) {
      finalRadius = maxRadius - angle;
      return finalRadius;
    } else {
      return finalRadius;
    }
  }

  render() {
    const data = this.props.data?.insightsByDate
      ? this.parseData(this.props.data.insightsByDate)
      : [];
    let radius = this.adjustBarShape(data.length);

    return (
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid vertical={false} strokeDasharray="0 0" fill="#f1f5fe" stroke="#fff" />
          <XAxis
            dataKey="day"
            axisLine={false}
            tickLine={false}
            tickCount={data.length}
            tick={{
              fontFamily: "Nunito, sans-serif",
              fontSize: "0.8125rem",
              fill: "#c8d1ec",
            }}
            padding={{ left: 20, right: 20 }}
          />
          <YAxis
            yAxisId="left"
            orientation="left"
            stroke="#8884d8"
            axisLine={false}
            tickLine={false}
            tick={{
              fontFamily: "Nunito, sans-serif",
              fontSize: "0.8125rem",
              fill: "#c8d1ec",
            }}
            tickFormatter={v => {
              if (v === 0) return this.props.data.averageEnergyChargedSession > 1 ? "kWh" : "Wh";
              else return v;
            }}
            padding={{ top: 50 }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#82ca9d"
            axisLine={false}
            tickLine={false}
            tick={{
              fontFamily: "Nunito, sans-serif",
              fontSize: "0.8125rem",
              fill: "#c8d1ec",
            }}
            tickFormatter={v => {
              const duration = moment.duration(v, "seconds");
              return duration.format(duration.asHours() >= 1 ? "h[h] m[m]" : "h[h] m[m] s[s]");
            }}
            padding={{ top: 50 }}
          />
          <Tooltip
            cursor={{ fill: "#e6eaf6" }}
            formatter={(value, name) => {
              const duration = moment.duration(value, "seconds");
              console.log(duration.asHours());

              return [
                name === "energy"
                  ? this.props.data.averageEnergyChargedSession > 1
                    ? `${value}kWh`
                    : `${value}Wh`
                  : `${duration.format(duration.asHours() >= 1 ? "h[h] m[m]" : "h[h] m[m] s[s]")}`,
                I18n.t("insightsByEV_" + name),
              ];
            }}
            contentStyle={{
              border: 0,
              borderRadius: "10px",
              boxShadow: "0 2px 10px #71778A30",
            }}
            labelStyle={{
              fontFamily: "Nunito, sans-serif",
              fontSize: "0.75rem",
              fontWeight: "700",
              color: "#353841",
            }}
            itemStyle={{
              fontFamily: "Nunito, sans-serif",
              fontSize: "0.75rem",
              color: "#71778a",
              padding: 0,
            }}
          />
          <Legend
            iconType="circle"
            formatter={value => <Translate className="legend" value={"insightsByEV_" + value} />}
            wrapperStyle={{ position: "relative", top: "-30px" }}
          />
          <Bar
            id="energy"
            yAxisId="left"
            dataKey="energy"
            fill="url(#energy)"
            shape={renderShape("energy", radius, this.props.brand?.colors?.secondaryColor)}
          />
          <Bar
            id="timeChanged"
            yAxisId="right"
            dataKey="timeCharged"
            fill={this.props.brand?.colors?.secondaryColor}
            shape={renderShape("time", radius, this.props.brand?.colors?.secondaryColor)}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }
}

const mapStateToProps = state => ({
  brand: state.app.brand,
});

export default connect(mapStateToProps, null)(Graph);
